import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auth, applyActionCode, checkActionCode } from '../config/firebase';
import CirclesLogo from '../components/CirclesLogo';

function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'already-verified'
  const [error, setError] = useState(null);
  const [email, setEmail] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      // Firebase passes the action code as 'oobCode' in the URL
      const oobCode = searchParams.get('oobCode');
      const mode = searchParams.get('mode');

      if (!oobCode) {
        setStatus('error');
        setError('Invalid verification link. Please request a new verification email.');
        return;
      }

      // Only handle email verification mode
      if (mode && mode !== 'verifyEmail') {
        setStatus('error');
        setError('Invalid link type.');
        return;
      }

      try {
        // First check what the action code is for
        const info = await checkActionCode(auth, oobCode);
        setEmail(info.data.email);

        // Apply the action code to verify the email
        await applyActionCode(auth, oobCode);
        setStatus('success');
      } catch (err) {
        console.error('Email verification error:', err);

        if (err.code === 'auth/invalid-action-code') {
          setStatus('error');
          setError('This link has expired or already been used. Please request a new verification email.');
        } else if (err.code === 'auth/expired-action-code') {
          setStatus('error');
          setError('This verification link has expired. Please request a new one.');
        } else {
          setStatus('error');
          setError('Something went wrong. Please try again or request a new verification email.');
        }
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleOpenApp = () => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor;
    const isAndroid = /android/i.test(userAgent);

    if (isAndroid) {
      // Use Android Intent for better deep linking with Play Store fallback
      const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.getcircles.circles';
      const intentUrl = `intent://email-verified#Intent;scheme=circles;package=com.getcircles.circles;S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};end`;
      window.location.href = intentUrl;
    } else {
      // iOS: Try deep link first, fall back to App Store
      const deepLink = 'circles://email-verified';
      const appStoreUrl = 'https://apps.apple.com/us/app/circles-meet-groups-together/id6757444421';

      window.location.href = deepLink;

      // Fallback to App Store after a delay (if app doesn't open)
      setTimeout(() => {
        window.location.href = appStoreUrl;
      }, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0b0b] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <CirclesLogo className="w-16 h-16" />
        </div>

        {status === 'verifying' && (
          <>
            <div className="mb-6">
              <div className="w-12 h-12 border-4 border-[#c8ff00] border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Verifying your email...
            </h1>
            <p className="text-gray-400">
              Please wait a moment.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="mb-6">
              <div className="w-16 h-16 bg-[#c8ff00] rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-[#0b0b0b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Email Verified!
            </h1>
            {email && (
              <p className="text-gray-400 mb-6">
                {email} has been verified.
              </p>
            )}
            <button
              onClick={handleOpenApp}
              className="w-full py-4 bg-[#c8ff00] text-[#0b0b0b] font-bold text-lg rounded-full hover:bg-[#d4ff33] transition-colors"
            >
              Open Circles App
            </button>
            <p className="text-gray-500 text-sm mt-4">
              Don't have the app?{' '}
              <a href="https://getcircles.app" className="text-[#c8ff00] hover:underline">
                Download it here
              </a>
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Verification Failed
            </h1>
            <p className="text-gray-400 mb-6">
              {error}
            </p>
            <button
              onClick={handleOpenApp}
              className="w-full py-4 bg-[#c8ff00] text-[#0b0b0b] font-bold text-lg rounded-full hover:bg-[#d4ff33] transition-colors"
            >
              Open Circles App
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyEmailPage;
